import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Input, Textarea } from '@heroui/react';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Dialog, DialogActions, DialogBody, DialogDescription } from '@/primitives/dialog.tsx';
import { Field, FieldErrorMessage, Label } from '@/primitives/fieldset.tsx';

export interface DescriptionFormDialogProps {
  title: string;
  dialogDescription?: string;
  initialValue: any;
  onClose: (value: string) => void;
  label: string;
  inputType?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isTextArea?: boolean;
  placeHolder?: string;
  isLoading?: boolean;
  submitButtonText?: string;
}

export const DescriptionFormDialog: FC<DescriptionFormDialogProps> = ({
  inputType = 'text',
  isOpen,
  setIsOpen,
  ...props
}) => {
  const { reset, handleSubmit, formState, register } = useForm<{
    description: string;
  }>({
    values: {
      description: props.initialValue,
    },
  });

  const onSubmit: SubmitHandler<{ description: string }> = async value => {
    await props.onClose(value.description);
    reset();
  };

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-row justify-between'>
          <h6 className='text-primary-600'>{props.title}</h6>
          <Button
            size='sm'
            isIconOnly
            variant='light'
            onClick={() => {
              reset();
              setIsOpen(false);
            }}
          >
            <XMarkIcon />
          </Button>
        </div>
        {props.dialogDescription && (
          <DialogDescription>{props.dialogDescription}</DialogDescription>
        )}
        <DialogBody>
          <Field>
            <Label required={true}>{props.label}</Label>
            {props.isTextArea ? (
              <Textarea
                variant='bordered'
                placeholder={props.placeHolder}
                isInvalid={!!formState.errors.description}
                {...register('description', {
                  required: 'This field is required',
                })}
                rows={5}
              />
            ) : (
              <Input
                variant='bordered'
                placeholder={props.placeHolder}
                type={inputType}
                isInvalid={!!formState.errors.description}
                {...register('description', {
                  required: 'This field is required',
                })}
              />
            )}
            <ErrorMessage
              name={'description'}
              as={<FieldErrorMessage />}
              errors={formState.errors}
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button
            type='submit'
            color='primary'
            variant='solid'
            isDisabled={!formState.isValid}
            isLoading={props.isLoading}
          >
            {props.submitButtonText || 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
